import { Color, Zone } from "@fairfleet/geotab";
import { ZoneDraft, ZoneDraftError } from "../models/ZoneDraft";
import { FALLBACK_ZONE_COLOR } from "./zoneColor";
import { geotabPointsToLngLat, lngLatToGeotabPoints, openRing } from "./zoneGeometry";
import { zoneTypeId } from "./zoneTypeId";

export const BUILT_IN_ZONE_TYPE_IDS = ["ZoneTypeCustomerId", "ZoneTypeOfficeId", "ZoneTypeHomeId"] as const;

/** Geotab's "no end" for activeTo. */
export const ZONE_ACTIVE_FOREVER = "2050-01-01T00:00:00.000Z";

export function isBuiltInZoneType(type: { id: string; isBuiltIn?: boolean }): boolean {
  return type.isBuiltIn === true || (BUILT_IN_ZONE_TYPE_IDS as readonly string[]).includes(type.id);
}

const toIso = (value: unknown): string =>
  value instanceof Date ? value.toISOString() : typeof value === "string" ? value : "";

const ids = (list: unknown[] | undefined): string[] =>
  (list ?? []).map(zoneTypeId).filter((id): id is string => id !== undefined);

export function emptyZoneDraft(groupIds: string[], color: Color): ZoneDraft {
  return {
    name: "",
    zoneTypeIds: [],
    points: [],
    comment: "",
    fillColor: color,
    groupIds,
    activeFrom: new Date().toISOString(),
    activeTo: ZONE_ACTIVE_FOREVER,
    displayed: true,
    mustIdentifyStops: true,
    externalReference: "",
  };
}

export function zoneToDraft(zone: Zone): ZoneDraft {
  return {
    id: zone.id,
    name: zone.name ?? "",
    zoneTypeIds: ids(zone.zoneTypes),
    points: openRing(geotabPointsToLngLat(zone.points)),
    comment: zone.comment ?? "",
    fillColor: zone.fillColor ?? FALLBACK_ZONE_COLOR,
    groupIds: ids(zone.groups),
    activeFrom: toIso(zone.activeFrom),
    activeTo: toIso(zone.activeTo) || ZONE_ACTIVE_FOREVER,
    displayed: zone.displayed !== false,
    mustIdentifyStops: zone.mustIdentifyStops !== false,
    externalReference: zone.externalReference ?? "",
  };
}

export function validateZoneDraft(draft: ZoneDraft): ZoneDraftError[] {
  const errors: ZoneDraftError[] = [];
  if (draft.name.trim() === "") errors.push("nameRequired");
  if (draft.zoneTypeIds.length === 0) errors.push("typeRequired");
  const distinct = new Set(openRing(draft.points).map(([lng, lat]) => `${lng},${lat}`));
  if (distinct.size < 3) errors.push("tooFewPoints");
  if (draft.activeFrom && draft.activeTo && Date.parse(draft.activeTo) <= Date.parse(draft.activeFrom)) {
    errors.push("activeRange");
  }
  return errors;
}

/** Fields Geotab computes from the points; sending stale values back would be misleading. */
const DERIVED_FIELDS = ["centroidLatitude", "centroidLongitude", "extent", "length"] as const;

/**
 * The entity for `Add` (no original) or `Set` (original). Starts from the full loaded zone so
 * fields this page does not edit – metadata, version, geometryType, anything MyGeotab set – survive.
 */
export function draftToZoneEntity(draft: ZoneDraft, original?: Zone): Record<string, unknown> {
  const entity: Record<string, unknown> = { ...(original ?? {}) };
  for (const field of DERIVED_FIELDS) delete entity[field];
  Object.assign(entity, {
    name: draft.name.trim(),
    comment: draft.comment,
    externalReference: draft.externalReference,
    zoneTypes: draft.zoneTypeIds.map(id => ({ id })),
    groups: draft.groupIds.map(id => ({ id })),
    points: lngLatToGeotabPoints(draft.points),
    fillColor: draft.fillColor,
    activeFrom: draft.activeFrom,
    activeTo: draft.activeTo,
    displayed: draft.displayed,
    mustIdentifyStops: draft.mustIdentifyStops,
  });
  if (draft.id) entity.id = draft.id;
  else delete entity.id;
  return entity;
}

export function countZonesByType(zones: Zone[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const zone of zones) {
    for (const id of new Set(ids(zone.zoneTypes))) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}
